import { useState } from "react";
import { motion } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { Reveal, LineReveal } from "@/components/Reveal";
import { Field, TextInput, SubmitButton, inputCls, HEAR_OPTIONS } from "@/components/forms";
import InviteShare from "@/components/InviteShare";
import { trackPlanVisitSubmit } from "@/lib/analytics";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const FAQS = [
  { q: "What should I wear?", a: "Come as you are. We care about you! Not your outfit." },
  { q: "What happens when I arrive?", a: "Our welcome team will greet you at the door, help you find a seat, and answer any questions. If you have kids, we'll walk you through VC Kids check-in together. No pressure, no spotlight - just a warm welcome." },
  { q: "How long is service?", a: "Plan on about 60 minutes. We start on time and respect your schedule." },
  { q: "What is worship like?", a: "Heartfelt, energetic worship that ministers to every age and walk of life. We encourage you to sing along, clap your hands, raise your hands and enjoy the presence of God." },
  { q: "What is the preaching like?", a: "Clear, Bible-centered and practical. Our pastor teaches straight from Scripture in a way that connects to real life - no theology degree required to follow along." },
  { q: "What about my kids?", a: "VC Kids provides a safe, clean, fun environment for nursery through elementary ages. Every volunteer is background-checked, and our secure check-in system means only you can pick up your child. Kids are also welcome in the main service if you prefer." },
  { q: "Where should I park?", a: "There's plenty of free parking close to the entrance near CR 220 & Henley Road. First-time guests get the best spots - look for the guest parking signs." },
  { q: "Do I have to give?", a: "No one has to give. We do encourage everyone in attendance to give a heartfelt offering to a loving God who gave us everything." },
  { q: "What if I haven't been to church in years?", a: "You'll be in good company. Many of us are starting over or starting fresh. There's no test at the door - just come as you are." },
  { q: "What if I don't know much about the Bible?", a: "Perfect. We teach the Bible in a way that makes sense whether it's your first Sunday or your five-hundredth. Questions are always welcome." },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
};

function PlanVisitForm() {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", visit_date: "", hear_about: "", connect_ok: false });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/plan-visit", form);
      trackPlanVisitSubmit();
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        className="rounded-3xl bg-navy p-10 text-center text-white shadow-lift" data-testid="plan-visit-success">
        <h3 className="font-display text-3xl font-800 tracking-tight">WE'LL SEE YOU SUNDAY!</h3>
        <p className="mt-4 text-white/75">We're looking forward to meeting you at Victory Chapel. Keep an eye on your inbox - we'll send everything you need before your visit.</p>
        <InviteShare />
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-3xl bg-white p-6 shadow-lift sm:p-10" data-testid="plan-visit-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="First Name" id="pv-first" required><TextInput id="pv-first" required value={form.first_name} onChange={set("first_name")} autoComplete="given-name" /></Field>
        <Field label="Last Name" id="pv-last" required><TextInput id="pv-last" required value={form.last_name} onChange={set("last_name")} autoComplete="family-name" /></Field>
        <Field label="Email" id="pv-email" required><TextInput id="pv-email" type="email" required value={form.email} onChange={set("email")} autoComplete="email" /></Field>
        <Field label="Mobile Phone" id="pv-phone"><TextInput id="pv-phone" type="tel" value={form.phone} onChange={set("phone")} autoComplete="tel" /></Field>
        <Field label="Planned Visit Date" id="pv-date" hint="Optional"><TextInput id="pv-date" type="date" value={form.visit_date} onChange={set("visit_date")} min={new Date().toISOString().split("T")[0]} /></Field>
        <div className="sm:col-span-2">
          <Field label="Where did you hear about us?" id="pv-hear" hint="Optional">
            <select id="pv-hear" data-testid="pv-hear" value={form.hear_about} onChange={set("hear_about")} className={inputCls}>
              <option value="">Select one...</option>
              {HEAR_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Field>
        </div>
        <label htmlFor="pv-connect" className="flex cursor-pointer items-start gap-3 text-sm text-slate-600 sm:col-span-2">
          <input id="pv-connect" type="checkbox" data-testid="pv-connect" checked={form.connect_ok}
                 onChange={(e) => setForm((f) => ({ ...f, connect_ok: e.target.checked }))}
                 className="mt-1 h-4 w-4 rounded border-slate-300 accent-[#C8102E]" />
          Yes, it's okay to connect with us ahead of time.
        </label>
      </div>
      <div className="mt-7">
        <SubmitButton testId="plan-visit-submit" loading={loading}>PLAN MY VISIT</SubmitButton>
        <p className="mt-4 text-xs leading-relaxed text-slate-500" data-testid="plan-visit-form-note">
          We'll reach out with everything you need - and on the day, we'll be watching for you at the door.
        </p>
      </div>
    </form>
  );
}

export default function ImNew() {
  return (
    <>
      <SEO
        title="I'm New - Plan Your Visit"
        description="Planning your first visit to Victory Chapel Clay? What to expect, service times, kids, and directions to our new church in Middleburg, FL - launching Jan 2027."
        path="/im-new"
        jsonLd={faqJsonLd}
      />
      <section className="grain relative overflow-hidden bg-navy py-20 text-white sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <p className="font-display text-sm font-700 uppercase tracking-[0.35em] text-rose-300">I'm New</p>
          <LineReveal className="mt-4 font-display text-5xl font-900 tracking-tighter sm:text-7xl" lines={["WE'D LOVE", "TO MEET YOU."]} />
          <Reveal delay={0.4}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg">
              Here's everything you need to know before your first Sunday at Victory Chapel -
              near CR 220 &amp; Henley Road in Clay County, Florida.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-20 sm:py-28" data-testid="imnew-faq-section">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <Reveal>
            <h2 className="font-display text-3xl font-800 tracking-tight text-navy sm:text-4xl text-balance">
              EVERY QUESTION YOU'RE ALREADY ASKING.
            </h2>
            <p className="mt-4 text-slate-600">Honest answers, no pressure. If we missed something, just ask.</p>
          </Reveal>
          <Reveal delay={0.15}>
            <Accordion type="single" collapsible className="mt-10" data-testid="imnew-faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-navy/10">
                  <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-display text-lg font-700 text-navy hover:text-victory">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-base leading-relaxed text-slate-600">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <section id="plan-your-visit" className="bg-cream py-20 sm:py-28" data-testid="plan-visit-section">
        <div className="mx-auto grid max-w-7xl items-start gap-12 px-5 sm:px-8 lg:grid-cols-2">
          <div className="lg:sticky lg:top-32">
            <Reveal>
              <h2 className="font-display text-4xl font-800 tracking-tight text-navy sm:text-5xl text-balance">
                PLAN YOUR VISIT.
              </h2>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-600 sm:text-lg">
                Tell us you're coming and we'll take care of the rest. We'll meet you at the door,
                help with the kids, and make sure your first Sunday feels like it
                was made for you - because it was.
              </p>
              <ul className="mt-8 space-y-3 text-sm text-slate-600">
                {["A personal welcome when you arrive", "Pre-register your kids for fast check-in", "No pressure - ever"].map((t) => (
                  <li key={t} className="flex items-center gap-3">
                    <span className="h-1.5 w-1.5 rounded-full bg-victory" aria-hidden="true" /> {t}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-8 overflow-hidden rounded-3xl shadow-lift">
                <img
                  src="/grand-opening.jpg"
                  alt="Victory Chapel - a new church coming to Clay County, launching January 2027, 3180 CR 220, Middleburg, Florida"
                  className="w-full"
                  width="1942"
                  height="809"
                  loading="lazy"
                  data-testid="grand-opening-poster"
                />
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <PlanVisitForm />
          </Reveal>
        </div>
      </section>
    </>
  );
}
